import { useCallback, useEffect } from 'react';

import type { CustomQueryPageParams, QueryPageReturnType } from '@luna/api';
import { type NewTransactionEventPayload, useNewTransactionEventThrottled } from '@api-client/sse';
import type { Transaction, TransactionListMetadata } from '@shared/transactions-types';
import { createPaginationEntity, request } from '@luna/api';
import { isFeatureEnabled } from '@luna/gatekeeper';
import { logger } from '@luna/logger';

import { pollForNewTransactions } from '../accounts-state-change/pollForNewTransactions';
import type { TransactionsUrlArgs } from '../TransactionsApiService';
import { matchRoundingTransactions } from '../RoundingTransactionsService';
import { fetchMultipleTransactions } from './fetchMultipleTransactions';
import { parseProductId } from './ProductSubscriptionService';
import { queryTransactionById } from '../transaction/queryTransactionById';
import { getTransactionUpdateUrl } from '../transaction/getTransactionUpdateUrl';
import { useClearTransactionsCacheIfNeeded } from '../cache/ClearTransactionsListCacheContext';
import { invalidateTransactionsMetadata } from '../probes/useTransactionsMetadata';

const PAGE_SIZE = 50;

type AdditionalData = {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    metadata?: TransactionListMetadata;
};

type UseTransactionsArgsType = TransactionsUrlArgs;

const {
    useEntities,
    useEntity: useTransaction,
    useEntityUpdate: useTransactionUpdate,
    entityHelpers: {
        updateCache: updateTransactionCache,
        invalidateAll: invalidateTransactions,
        invalidateLists: invalidateTransactionsLists,
    },
} = createPaginationEntity({
    name: '@transactions/paginated',
    payloadCreators: {
        queryPage,
        queryById: queryTransactionById,
        updateById: updateTransactionById,
    },
});

export {
    useTransaction,
    useTransactionUpdate,
    updateTransactionCache,
    invalidateTransactions,
    invalidateTransactionsLists,
};

export function useTransactions(
    queryPrams: UseTransactionsArgsType,
): ReturnType<typeof useEntities> {
    const clearTransactionsCacheIfNeeded = useClearTransactionsCacheIfNeeded();
    clearTransactionsCacheIfNeeded();

    const { productId } = queryPrams;
    const transactionsQuery = useEntities(queryPrams);
    const { refetch } = transactionsQuery;

    const isGlobalSseEnabled = isFeatureEnabled((f) => f.globalSseNotifications);

    const reloadTransactions = useCallback(
        (eventPayloads: NewTransactionEventPayload[][]) => {
            const currentProductId = parseProductId(productId);
            const containsCurrentProductId = eventPayloads
                .flat()
                .some((payload) => payload.productId === currentProductId);

            if (containsCurrentProductId) {
                invalidateTransactionsLists();
                invalidateTransactionsMetadata();
            }
        },
        [productId],
    );

    useNewTransactionEventThrottled(reloadTransactions);

    if (!isGlobalSseEnabled) {
        useEffect(
            () =>
                productId
                    ? pollForNewTransactions(productId, () => {
                          refetch().catch(logger.error);
                          invalidateTransactionsMetadata();
                      })
                    : undefined,
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [productId],
        );
    }

    return transactionsQuery;
}

async function queryPage(
    pageParams: CustomQueryPageParams<TransactionsUrlArgs> = {},
): Promise<QueryPageReturnType<Transaction, AdditionalData>> {
    const {
        productId,
        searchState,
        sort,
        isWithSum = false,
        isWithSumWithRoundup = false,
        isWithCount = false,
        isWithBalance = false,
        isWithProducts = false,
        pageSize = PAGE_SIZE,
        pageNumber,
    } = pageParams;

    const {
        collection = [],
        currentPage,
        totalPages,
        totalElements,
        metadata,
    } = await fetchMultipleTransactions({
        productId,
        searchState,
        sort,
        page: pageNumber,
        pageSize,
        isWithSum,
        isWithCount,
        isWithBalance,
        isWithSumWithRoundup,
        isWithProducts,
    });

    return {
        data: matchRoundingTransactions(collection),
        pageNumber: currentPage,
        pageCount: totalPages,
        additionalData: {
            currentPage,
            totalPages,
            totalElements,
            metadata: metadata || undefined,
        },
    };
}

type QueryUpdateByIdParams = { id: string; changes: Transaction };

async function updateTransactionById({
    changes: transaction,
}: QueryUpdateByIdParams): Promise<Transaction> {
    const updateUrl = getTransactionUpdateUrl(transaction);
    try {
        const savedTransaction = await request<Transaction>(updateUrl, {
            method: 'PUT',
            body: JSON.stringify(transaction),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        /*
         * Merge the saved transaction with the original transaction to preserve
         * any additional client-side information, such as round-up details.
         *
         * @see RoundingTransactionsService.ts
         * */
        return { ...transaction, ...savedTransaction };
    } catch (error) {
        logger.error(error);
        throw error;
    }
}
